import moment from 'moment'
// jsPDF is pinned at 1.5.3 in this app, so the default export is used (not the
// named `{ jsPDF }` of 2.x/3.x), and jspdf-autotable is pinned to 3.5.x, the last
// line supporting it. Both expose `autoTable(doc, options)` and
// `doc.lastAutoTable.finalY` — the same idiom the Fuel report uses.
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { LOGO_ON_NAVY, LOGO_W, LOGO_H } from './brandLogo'
// Emoji and characters Helvetica can't print (names, locations) -> util/pdfText.js
import { createPdfTextTracker, toPdfText, unprintableNote } from '../../util/pdfText'
import { missingByValue } from './reconSummary'

/**
 * Management report for a completed reconciliation.
 *
 * Order (Rishabh, 2026-10-27). Page 1 carries two lines whose parts add up to their
 * totals exactly (figures from reconSummary.js, shared with the Excel export):
 *  1. total assets as on date (today, without SOLD) = register on the audit date
 *     (without the assets marked unsold) + marked unsold during the audit
 *     + new assets added during the audit + bought after the audit date
 *  2. assets as on the last audit date (except sold) = found + recovered via PV
 *     + missing + bought after location audit + out of scope + new assets added
 *     + marked unsold
 * then the outcome table with invoice value. After it: everything physically
 * audited by action taken, with value; the complete missing list (highest invoice
 * value first); the location -> sub-location summary; and the annexure - audit
 * period and coverage, then every AST scan and PV item with the asset it became and
 * the document Process raised (PI, ATA/ARN, SI). The AST / PV bridge tables and the
 * reconcile-status table were dropped from the PDF (still in the Excel summary).
 *
 * STYLING follows "AssetPro Design Foundations" — the light-mode tokens, the type
 * hierarchy and the 4px spacing scale. One deviation, forced:
 *  - Typeface is Helvetica, not IBM Plex Sans. jsPDF 1.5.3 can only embed a TTF
 *    and no IBM Plex TTF ships with this app; Helvetica is the closest built-in
 *    grotesque. Drop a TTF in and swap it here if brand-exact type is needed.
 * The wordmark is the on-navy variant from the brand folder (see brandLogo.js).
 *
 * Data reliability — this goes to management, so:
 *  - Every figure is computed from the loaded voucher; nothing is estimated.
 *  - Where a value cannot be known it says so in words rather than printing 0.
 *  - Assets with no invoice on record are counted apart from the total, so a
 *    partial value is never read as complete.
 *  - If a line does not add up, the report says so in a warning box instead of
 *    printing a total that hides the gap.
 *  - The audit period comes from real scan timestamps, and the report names them.
 */

// ---- AssetPro Design Foundations, light mode ----
const C = {
  navy: [10, 18, 32],          // --brand-navy
  brand: [255, 107, 25],       // --brand / orange-500
  azure: [14, 111, 224],       // --azure-600
  success: [31, 168, 98],      // --success-600
  critical: [226, 59, 65],     // --critical-600
  warning: [217, 138, 0],      // --warning-600
  strong: [11, 27, 46],        // --text-strong
  body: [34, 52, 72],          // --text-body
  muted: [86, 101, 119],       // --text-muted
  subtle: [124, 138, 153],     // --text-subtle
  raised: [241, 244, 249],     // --surface-raised
  app: [235, 238, 244],        // --surface-app
  border: [227, 232, 239],     // --border-subtle, flattened
  ink0: [245, 249, 253],       // --ink-0
  ink2: [156, 178, 201]        // --ink-2
}
// --space scale (4px base), in points
const S = { 1: 4, 2: 8, 3: 12, 4: 16, 5: 20, 6: 24, 7: 32, 8: 40 }

const INR = (n) => 'Rs ' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })
const NUM = (n) => Number(n || 0).toLocaleString('en-IN')
const DATE = (d) => (d ? moment(d).format('DD MMM YYYY') : '—')

export const buildAuditReport = ({
  voucherLabel, orgLabel, astData, pvData, assetQueue, summary,
  locationCoverage, excludedLocs, excludedSubs, locName, clientName, placeOf,
  auditCutoff, postAuditCount, annexure
}) => {
  // Compressed: with the annexure the report runs to ~260 pages (12 MB uncompressed).
  const doc = new jsPDF({ unit: 'pt', format: 'a4', compress: true })
  doc.setFont('helvetica')
  const W = doc.internal.pageSize.getWidth()
  const H = doc.internal.pageSize.getHeight()
  const M = S[8]                       // 40pt page margin
  const CW = W - M * 2
  const BOTTOM = H - 50                // keep clear of the footer
  // Every data-bearing string and table cell is cleaned and counted (see util/pdfText.js).
  const pdf = createPdfTextTracker()

  // The wordmark is decoration: if it ever fails to decode, the report must still
  // produce rather than throwing on the way to management.
  const LOGO_PT_W = 132
  const drawLogo = (x, yTop, w) => {
    try { doc.addImage(LOGO_ON_NAVY, 'JPEG', x, yTop, w, w * (LOGO_H / LOGO_W)) }
    catch (e) { /* wordmark is optional */ }
  }

  const fill = (c) => doc.setFillColor(c[0], c[1], c[2])
  const ink = (c) => doc.setTextColor(c[0], c[1], c[2])
  const draw = (c) => doc.setDrawColor(c[0], c[1], c[2])

  const gridStyle = {
    theme: 'grid',
    styles: { fontSize: 8.5, cellPadding: 4.5, textColor: C.body, lineColor: C.border, lineWidth: 0.5, font: 'helvetica' },
    headStyles: { fillColor: C.navy, textColor: C.ink0, fontStyle: 'bold', fontSize: 8 },
    margin: { left: M, right: M }
  }
  // Table rows are built as { cells, kind }: kind 'total' rows are bold and shaded,
  // 'loc' / 'sub' rows are the location table's two levels. body() strips the kind
  // for autoTable and rowHook() looks it up again by row index.
  const body = (rows) => rows.map(r => r.cells)
  const rowHook = (rows, extra) => pdf.cellHook((d) => {
    const kind = d.section === 'body' && rows[d.row.index] ? rows[d.row.index].kind : null
    if (kind === 'total') {
      d.cell.styles.fontStyle = 'bold'; d.cell.styles.fillColor = C.app; d.cell.styles.textColor = C.strong
    }
    if (extra) extra(d, kind)
  })
  const row = (cells, kind) => ({ cells, kind })

  // ---------- derived: audit period from real timestamps ----------
  const stamps = []
  ;(astData || []).forEach(r => { if (r.time) stamps.push(new Date(r.time)) })
  ;(pvData || []).forEach(r => { if (r.date) stamps.push(new Date(r.date)) })
  stamps.sort((a, b) => a - b)
  const first = stamps[0], last = stamps[stamps.length - 1]
  const activeDays = new Set(stamps.map(d => moment(d).format('YYYY-MM-DD'))).size
  const elapsed = (first && last)
    ? moment(last).startOf('day').diff(moment(first).startOf('day'), 'days') + 1 : 0

  // ---------- derived: coverage ----------
  const inScope = locationCoverage.filter(l => !excludedLocs.has(l.loc))
  const outScope = locationCoverage.filter(l => excludedLocs.has(l.loc))
  const subsInScope = inScope.reduce(
    (n, l) => n + l.subs.filter(sv => !excludedSubs.has(`${l.loc}|${sv.client}`)).length, 0)
  const subsTotal = locationCoverage.reduce((n, l) => n + l.subs.length, 0)
  const scannedLocs = new Set()
  ;(astData || []).forEach(r => scannedLocs.add(String(r.location)))
  ;(pvData || []).forEach(r => scannedLocs.add(String(r.location)))

  // ---------- derived: value cells ----------
  const { register, today, closing, outcome, actionRows, audit, newAssets, statusRows, locations } = summary
  const anyValue = Object.keys(outcome).some(k => outcome[k].valued > 0) || statusRows.some(r => r.valued > 0)
  const valueCell = (b) => {
    if (!anyValue) return 'not available'
    if (b.count === 0) return '—'
    if (b.valued === 0) return 'no invoice on record'
    return INR(b.value) + (b.valued < b.count ? `  (${NUM(b.valued)} of ${NUM(b.count)} priced)` : '')
  }
  const closingValue = Object.keys(closing.parts).reduce((acc, k) => ({
    count: acc.count + closing.parts[k].count, valued: acc.valued + closing.parts[k].valued, value: acc.value + closing.parts[k].value
  }), { count: 0, valued: 0, value: 0 })
  const auditDate = auditCutoff ? DATE(auditCutoff.date) : null

  // A red box for any line that does not add up - never a silent mismatch.
  const warnBox = (title, text) => {
    const lines = doc.splitTextToSize(text, CW - S[3] * 2)
    const h = 22 + lines.length * 10
    if (y + h > BOTTOM) { doc.addPage(); y = S[8] }
    fill([253, 236, 237]); draw(C.critical); doc.setLineWidth(0.7)
    doc.rect(M, y, CW, h, 'FD')
    ink(C.critical); doc.setFontSize(8.5); doc.setFontType('bold')
    doc.text(title, M + S[3], y + 14)
    ink(C.body); doc.setFontSize(8); doc.setFontType('normal')
    doc.text(lines, M + S[3], y + 26)
    y += h + S[3]
  }
  const heading = (n, title, sub) => {
    if (y + 90 > BOTTOM) { doc.addPage(); y = S[8] }
    if (n) {
      fill(C.brand); doc.rect(M, y - 10, 16, 14, 'F')
      ink([255, 255, 255]); doc.setFontSize(9); doc.setFontType('bold')
      doc.text(String(n), M + 8, y, { align: 'center' })
    }
    ink(C.strong); doc.setFontSize(12); doc.setFontType('bold')
    doc.text(title, M + (n ? 24 : 0), y)
    if (sub) {
      ink(C.muted); doc.setFontSize(8); doc.setFontType('normal')
      const lines = doc.splitTextToSize(pdf.clean(sub), CW)
      doc.text(lines, M, y + 13)
      y += 13 + (lines.length - 1) * 10
    }
    y += S[3]
  }
  // A row of figure tiles joined by operators: [total] = [a] + [b] + ...
  // perRow wraps a long line onto further rows; a wrapped row opens with its '+'.
  // Returns each tile's box, for marking a group of tiles afterwards (see bracket).
  const equation = (tiles, perRow) => {
    const OP = 14
    const n = perRow || tiles.length
    const tw = (CW - OP * (n - 1)) / n
    const th = 56
    const boxes = []
    tiles.forEach((t, i) => {
      if (i > 0 && i % n === 0) y += th + S[2]
      const x = M + (i % n) * (tw + OP)
      boxes.push({ x, y, w: tw, h: th })
      if (i > 0) {
        ink(C.muted); doc.setFontSize(13); doc.setFontType('bold')
        doc.text(i === 1 ? '=' : '+', x - OP / 2, y + th / 2 + 4, { align: 'center' })
      }
      fill(t.total ? C.navy : C.raised); draw(t.total ? C.navy : C.border); doc.setLineWidth(0.7)
      doc.rect(x, y, tw, th, 'FD')
      if (!t.total) { fill(t.c); doc.rect(x, y, 3, th, 'F') }
      ink(t.total ? C.ink0 : t.c); doc.setFontSize(17); doc.setFontType('bold')
      doc.text(NUM(t.v), x + S[3], y + 26)
      ink(t.total ? C.ink2 : C.muted); doc.setFontSize(6.8); doc.setFontType('normal')
      doc.text(doc.splitTextToSize(t.label.toUpperCase(), tw - S[3] - 4).slice(0, 2), x + S[3], y + 39, { lineHeightFactor: 1.1 })
    })
    y += th + S[3]
    return boxes
  }
  // A bracket under tiles a..b of one row, with a label beneath its middle.
  const bracket = (a, b, label) => {
    const x1 = a.x, x2 = b.x + b.w, yb = a.y + a.h + 5
    draw(C.navy); doc.setLineWidth(0.8)
    doc.line(x1, yb, x2, yb)
    doc.line(x1, yb - 3, x1, yb); doc.line(x2, yb - 3, x2, yb)
    doc.line((x1 + x2) / 2, yb, (x1 + x2) / 2, yb + 4)
    ink(C.navy); doc.setFontSize(7.5); doc.setFontType('bold')
    doc.text(pdf.clean(label), (x1 + x2) / 2, yb + 13, { align: 'center' })
    y = yb + 13 + S[3]
  }

  const sectionHead = (title, sub) => {
    fill(C.navy); doc.rect(0, 0, W, 58, 'F')
    fill(C.brand); doc.rect(0, 58, W, 2.5, 'F')
    ink(C.ink0); doc.setFontSize(14); doc.setFontType('bold')
    doc.text(title, M, 30)
    // Wrapped clear of the logo; a second line still fits inside the 58pt band.
    if (sub) { ink(C.ink2); doc.setFontSize(8); doc.setFontType('normal'); doc.text(doc.splitTextToSize(sub, CW - 110).slice(0, 2), M, 44, { lineHeightFactor: 1.15 }) }
    drawLogo(W - M - 96, 18, 96)
    return 58 + S[6]
  }

  // ================= page 1 =================
  // Navy masthead with the brand rule beneath — the foundations' brand pairing.
  fill(C.navy); doc.rect(0, 0, W, 104, 'F')
  fill(C.brand); doc.rect(0, 104, W, 3, 'F')

  ink(C.ink0); doc.setFontSize(21); doc.setFontType('bold')
  doc.text('Asset Reconciliation Report', M, 46)
  ink(C.ink2); doc.setFontSize(10); doc.setFontType('normal')
  doc.text(pdf.clean([orgLabel, `Audit voucher ${voucherLabel || '—'}`].filter(Boolean).join('   ·   ')), M, 66)
  doc.setFontSize(8.5)
  doc.text(`Generated ${moment().format('DD MMM YYYY, HH:mm')}`, M, 84)
  drawLogo(W - M - LOGO_PT_W, 30, LOGO_PT_W)

  let y = 104 + S[7] + S[2]

  // ---- line 1: the register today, from the audit date ----
  // Starts from the register without the SOLD location: sold assets are not part of
  // the register (Rishabh, 2026-10-24). The SOLD count stays in the Excel summary.
  heading(1, 'Total assets as on date',
    `The register today (${moment().format('DD MMM YYYY')}, excluding the SOLD location)` +
    (auditDate ? `, from the register on ${auditDate}, the last scan of this audit.` : '. No scans recorded, so there is no audit date.'))
  const line1 = [
    { label: 'Total assets as on date', v: today.total, total: true },
    { label: auditDate ? `Register on ${auditDate}` : 'Register (no audit date)', v: today.onAuditDate, c: C.strong },
    { label: 'Marked unsold during the audit', v: today.unsold, c: C.success },
    { label: 'New assets added during the audit', v: today.created, c: C.azure },
    { label: 'Bought after the audit date', v: today.afterAuditDate, c: C.warning }
  ]
  if (today.otherStatus) line1.push({ label: 'Unrecognised status', v: today.otherStatus, c: C.critical })
  const l1 = equation(line1)
  // Tiles 2-4 make line 2's total; mark them so 8,986-style totals are not a puzzle (Rishabh, 2026-10-27).
  bracket(l1[1], l1[3], `${NUM(today.onAuditDate)} + ${NUM(today.unsold)} + ${NUM(today.created)} = ${NUM(closing.fromToday)}, the total of line 2 below`)
  if (today.check !== 0) {
    warnBox('Line 1 does not add up',
      `The register today (${NUM(today.total)}) differs from its parts by ${NUM(today.check)}. Do not rely on these totals until this is investigated.`)
  }

  // ---- line 2: assets on the last audit date, including what the audit added ----
  y += S[2]
  heading(2, 'Assets as on the last date of audit (except sold)',
    `${NUM(closing.total)} = ${NUM(today.onAuditDate)} register on ${auditDate || 'the audit date'} + ${NUM(today.unsold)} marked unsold + ` +
    `${NUM(today.created)} new assets added during the audit (line 1: ${NUM(today.total)} less ${NUM(today.afterAuditDate)} bought after the audit date)` +
    (today.otherStatus ? ` less ${NUM(today.otherStatus)} with an unrecognised status` : '') + '. What became of each:')
  if (closing.todayCheck !== 0) {
    warnBox('Line 2 does not follow from line 1',
      `Line 2 (${NUM(closing.total)}) differs from register on the audit date + marked unsold + new assets (${NUM(closing.fromToday)}) by ${NUM(closing.todayCheck)}.`)
  }
  equation([
    { label: auditDate ? `Assets as on ${auditDate}` : 'Assets (no audit date)', v: closing.total, total: true },
    { label: 'Found in audit', v: closing.parts.found.count, c: C.success },
    { label: 'Recovered via PV', v: closing.parts.pvlink.count, c: C.azure },
    { label: 'Missing', v: closing.parts.missing.count, c: C.critical },
    { label: 'Bought after location audit', v: closing.parts.boughtafter.count, c: C.warning },
    { label: 'Out of scope', v: closing.parts.oos.count, c: C.subtle },
    { label: 'New assets added during audit', v: closing.parts.newasset.count, c: C.azure },
    { label: 'Marked unsold (sale reversed)', v: closing.parts.unsold.count, c: C.success }
  ], 4)
  if (closing.check !== 0) {
    warnBox('Line 2 does not add up',
      `Its parts differ from the assets on the last audit date (${NUM(closing.total)}) by ${NUM(closing.check)}.`)
  }

  // ---- outcome, with invoice value ----
  y += S[2]
  if (y + 220 > BOTTOM) { doc.addPage(); y = S[8] }
  const outRows = [
    row(['Found in audit', NUM(closing.parts.found.count), valueCell(closing.parts.found)]),
    row(['Recovered — linked from a PV item', NUM(closing.parts.pvlink.count), valueCell(closing.parts.pvlink)]),
    row(['Missing', NUM(closing.parts.missing.count), valueCell(closing.parts.missing)]),
    row(['Bought after its location was audited (not missing)', NUM(closing.parts.boughtafter.count), valueCell(closing.parts.boughtafter)]),
    row(['Out of scope', NUM(closing.parts.oos.count), valueCell(closing.parts.oos)]),
    row(['New assets added during the audit', NUM(closing.parts.newasset.count), valueCell(closing.parts.newasset)]),
    row(['Marked unsold — sale reversed by this audit', NUM(closing.parts.unsold.count), valueCell(closing.parts.unsold)]),
    row([auditDate ? `Assets as on ${auditDate} (line 2)` : 'Assets (line 2)', NUM(closingValue.count), valueCell(closingValue)], 'total'),
    row(['Bought after the audit date (line 1, outside this audit)', NUM(today.afterAudit.count), valueCell(today.afterAudit)])
  ]
  ink(C.strong); doc.setFontSize(11); doc.setFontType('bold')
  doc.text('Outcome', M, y)
  autoTable(doc, {
    ...gridStyle,
    startY: y + S[2],
    alternateRowStyles: { fillColor: [250, 251, 253] },
    head: [['Outcome', 'Assets', 'Invoice value']],
    body: body(outRows),
    columnStyles: { 1: { halign: 'right', cellWidth: 60 }, 2: { halign: 'right', cellWidth: 170 } },
    didParseCell: rowHook(outRows, (d) => {
      if (d.section === 'body' && d.row.index === outRows.length - 1) { d.cell.styles.textColor = C.muted; d.cell.styles.fontStyle = 'italic' }
    })
  })
  y = doc.lastAutoTable.finalY + S[2]
  ink(C.subtle); doc.setFontSize(7.5); doc.setFontType('normal')
  const outNote = doc.splitTextToSize(
    'Invoice value is the purchase rate on the asset\'s purchase order; assets with none are counted apart, never as zero. ' +
    'New assets were created with a purchase rate of 0 as a placeholder; that 0 is shown as "no invoice on record". ' +
    (newAssets.pending ? `${NUM(newAssets.pending)} PV item(s) set to create a new asset are not processed yet and are not counted above.` : ''), CW)
  doc.text(outNote, M, y + 8)
  y += 8 + outNote.length * 9 + S[2]

  if (!anyValue) {
    if (y + 34 > BOTTOM) { doc.addPage(); y = S[8] }
    fill([255, 247, 235]); draw(C.warning); doc.setLineWidth(0.7)
    doc.rect(M, y, CW, 34, 'FD')
    ink(C.warning); doc.setFontSize(8.5); doc.setFontType('bold')
    doc.text('Invoice value is not available', M + S[3], y + 14)
    doc.setFontType('normal'); ink(C.body); doc.setFontSize(8)
    doc.text('The reconciliation endpoint does not return invoiceValue, so these assets cannot be valued. This is a data gap, not a value of zero.',
      M + S[3], y + 26)
    y += 34 + S[3]
  }

  // ================= physically audited: action taken, with value =================
  doc.addPage()
  y = sectionHead('Physically audited',
    'Every AST scan and every PV item recorded in this audit, the action taken on it and the invoice value of the assets it concerns.')
  equation([
    { label: 'Records physically audited', v: audit.ast + audit.pv, total: true },
    { label: 'AST scans', v: audit.ast, c: C.success },
    { label: 'PV items', v: audit.pv, c: C.azure }
  ])
  const actTot = actionRows.reduce((t, a) => ({
    ast: t.ast + a.ast, pv: t.pv + a.pv, p: t.p + a.astProcessed + a.pvProcessed
  }), { ast: 0, pv: 0, p: 0 })
  const actValue = (a) => (a.assets === 0 ? 'no register asset' : valueCell({ count: a.assets, valued: a.valued, value: a.value }))
  const actRows = actionRows.map(a => row([a.label, NUM(a.ast), NUM(a.pv), NUM(a.ast + a.pv), NUM(a.astProcessed + a.pvProcessed), NUM(a.assets), actValue(a)]))
    .concat([row(['Total', NUM(actTot.ast), NUM(actTot.pv), NUM(actTot.ast + actTot.pv), NUM(actTot.p), '', ''], 'total')])
  autoTable(doc, {
    ...gridStyle,
    startY: y,
    head: [['Action taken', 'AST', 'PV', 'Total', 'Processed', 'Assets', 'Invoice value']],
    body: body(actRows),
    columnStyles: {
      1: { halign: 'right', cellWidth: 42 }, 2: { halign: 'right', cellWidth: 42 }, 3: { halign: 'right', cellWidth: 42 },
      4: { halign: 'right', cellWidth: 50 }, 5: { halign: 'right', cellWidth: 42 }, 6: { halign: 'right', cellWidth: 172 }
    },
    didParseCell: rowHook(actRows)
  })
  y = doc.lastAutoTable.finalY + S[2]
  ink(C.subtle); doc.setFontSize(7.5); doc.setFontType('normal')
  doc.text(doc.splitTextToSize(
    'Assets = distinct register assets the records of that action concern: the asset scanned (AST), the asset a PV item was linked to, ' +
    'or the assets a "new asset" created. Each is valued once within its row; an asset can appear under two actions, so the value column is not totalled. ' +
    `New assets: ${NUM(newAssets.created)} created in the register (${NUM(newAssets.fromPv)} from PV items, ${NUM(newAssets.fromAst)} from repeat AST scans)` +
    (newAssets.pending ? `; ${NUM(newAssets.pending)} PV item(s) set to create a new asset are not processed yet.` : '.') +
    ' A PV item of quantity more than one creates several assets.', CW), M, y + 8)

  // ================= the missing list =================
  // Complete, highest invoice value first; no invoice value on record last (Rishabh, 2026-10-27).
  const missing = missingByValue(assetQueue.filter(r => r.assetStatus === 'missing'))
  doc.addPage()
  // Assets bought after the audit's last capture are outside its purview and are
  // not in this list; say how many, so the count is never read as complete.
  const cutoffNote = auditCutoff
    ? ` Excluded as bought after the audit date ${DATE(auditCutoff.date)}: ${NUM(postAuditCount || 0)}.`
    : ''
  y = sectionHead(`Missing assets — ${NUM(missing.length)}`,
    'Complete list, highest invoice value first; assets with no invoice value on record are listed last.' + cutoffNote)

  autoTable(doc, {
    startY: y,
    theme: 'grid',
    styles: { fontSize: 7, cellPadding: 3, textColor: C.body, lineColor: C.border, lineWidth: 0.5, font: 'helvetica' },
    headStyles: { fillColor: C.critical, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7 },
    alternateRowStyles: { fillColor: [252, 250, 250] },
    head: [['Asset', 'Equipment', 'Location on audit date', 'Sub-location', 'Last located', 'Invoice value']],
    body: missing.map(r => {
      const p = placeOf(r)
      return [
        r.assetCode || '',
        (r.assetModel && r.assetModel.name) || '',
        locName(p.loc) || '',
        clientName(p.cli) || '',
        r.lastLocated ? DATE(r.lastLocated) : 'never located',
        r.invoiceValue != null ? INR(r.invoiceValue) : (anyValue ? 'no invoice' : 'n/a')
      ]
    }),
    columnStyles: { 4: { cellWidth: 64 }, 5: { halign: 'right', cellWidth: 66 } },
    didParseCell: pdf.cellHook((d) => {
      if (d.section === 'body' && d.column.index === 4 && d.cell.raw === 'never located') {
        d.cell.styles.textColor = C.subtle; d.cell.styles.fontStyle = 'italic'
      }
    }),
    margin: { left: M, right: M }
  })

  // ================= location -> sub-location =================
  doc.addPage()
  y = sectionHead('Location-wise summary',
    'Register on the audit date with the assets marked unsold, by location and sub-location, A-Z. Each location total is the sum of its sub-locations.')

  const cov = (t) => {
    // Assets the audit could have found: not out of scope, not bought after the place was audited.
    const scoped = t.assets - t.oos - t.boughtafter
    return scoped > 0 ? Math.round(((t.found + t.pvlink) / scoped) * 100) + '%' : '—'
  }
  const locCells = (name, t) => [name, NUM(t.assets), NUM(t.found), NUM(t.pvlink), NUM(t.missing), NUM(t.boughtafter), NUM(t.oos), cov(t)]
  const locRows = []
  locations.rows.forEach(L => {
    locRows.push(row(locCells(L.name, L.totals), 'loc'))
    L.subs.forEach(s => locRows.push(row(locCells(s.name, s.totals), 'sub')))
  })
  if (!locRows.length) locRows.push(row(['No register assets on the audit date', '', '', '', '', '', '', '']))
  locRows.push(row(locCells('Total — register on the audit date + marked unsold', locations.total), 'total'))

  autoTable(doc, {
    ...gridStyle,
    startY: y,
    styles: { ...gridStyle.styles, fontSize: 7.6, cellPadding: 3.2 },
    headStyles: { ...gridStyle.headStyles, fontSize: 7.3 },
    head: [['Location / sub-location', 'Assets', 'Found', 'Via PV', 'Missing', 'Bought after audit', 'Out of scope', 'Coverage']],
    body: body(locRows),
    columnStyles: {
      1: { halign: 'right', cellWidth: 40 }, 2: { halign: 'right', cellWidth: 40 }, 3: { halign: 'right', cellWidth: 40 },
      4: { halign: 'right', cellWidth: 44 }, 5: { halign: 'right', cellWidth: 52 }, 6: { halign: 'right', cellWidth: 46 },
      7: { halign: 'right', cellWidth: 48 }
    },
    didParseCell: rowHook(locRows, (d, kind) => {
      if (d.section !== 'body') return
      if (kind === 'loc') {
        d.cell.styles.fontStyle = 'bold'; d.cell.styles.fillColor = C.raised; d.cell.styles.textColor = C.strong
      } else if (kind === 'sub' && d.column.index === 0) {
        d.cell.styles.textColor = C.muted
        d.cell.styles.cellPadding = { top: 3.2, bottom: 3.2, right: 3.2, left: 16 }
      }
      // Missing is the number management acts on, so it carries the critical colour.
      if (d.column.index === 4 && d.cell.raw !== '0' && d.cell.raw !== '') {
        d.cell.styles.textColor = C.critical; d.cell.styles.fontStyle = 'bold'
      }
    }),
    margin: { left: M, right: M }
  })
  y = doc.lastAutoTable.finalY + S[2]
  ink(C.subtle); doc.setFontSize(7.5); doc.setFontType('normal')
  doc.text(doc.splitTextToSize('Coverage = (found + via PV) / (assets - bought after audit - out of scope). "Bought after audit" = bought after the place\'s last scan. ' +
    '"(no sub-location)" is listed last under its location.', CW), M, y + 8)
  if (locations.total.assets !== register.total) {
    y += S[5]
    warnBox('Location table does not add up',
      `Its total (${NUM(locations.total.assets)}) differs from the register on the audit date plus the assets marked unsold (${NUM(register.total)}).`)
  }

  // ================= annexure: every physical record =================
  // Each AST scan and PV item, grouped by what Process did with it, with the document
  // it raised exactly as recorded (processed remark). Sections and their counts open
  // the annexure, and must add up to the records physically audited.
  let annexRows = 0
  if (annexure) {
    const { sections, scanned, audited } = annexure
    doc.addPage()
    y = sectionHead('Annexure — physical record of the reconciliation',
      'Every AST scan and PV item, the asset it became and the document raised (PI = new asset, ATA/ARN = transfer, ' +
      'SI = sale reversed). By location, sub-location, audit date.')
    // The audit period and coverage open the annexure: the context for the records below.
    // ---- two info blocks ----
    const infoBlock = (x, w, title, rows) => {
      ink(C.strong); doc.setFontSize(11); doc.setFontType('bold')
      doc.text(title, x, y)
      autoTable(doc, {
        startY: y + S[2],
        theme: 'plain',
        styles: { fontSize: 8.5, cellPadding: 3, textColor: C.body, font: 'helvetica' },
        body: rows,
        columnStyles: { 0: { cellWidth: w * 0.52, textColor: C.muted }, 1: { fontStyle: 'bold' } },
        margin: { left: x, right: W - x - w },
        tableWidth: w,
        didParseCell: pdf.cellHook()
      })
      return doc.lastAutoTable.finalY
    }
    const colW = (CW - S[6]) / 2
    const y1 = infoBlock(M, colW, 'The audit', [
      ['First scan', first ? moment(first).format('DD MMM YYYY, HH:mm') : 'no scans recorded'],
      ['Last scan', last ? moment(last).format('DD MMM YYYY, HH:mm') : '—'],
      ['Elapsed', elapsed ? `${NUM(elapsed)} day${elapsed === 1 ? '' : 's'}` : '—'],
      ['Days with activity', activeDays ? NUM(activeDays) : '—'],
      ['Records captured', `${NUM((astData || []).length)} AST · ${NUM((pvData || []).length)} PV`]
    ])
    const y2 = infoBlock(M + colW + S[6], colW, 'Coverage', [
      ['Locations in register', NUM(locationCoverage.length)],
      ['Declared in scope', `${NUM(inScope.length)} (${NUM(outScope.length)} excluded)`],
      ['Sub-locations in scope', `${NUM(subsInScope)} of ${NUM(subsTotal)}`],
      ['Locations that saw a scan', NUM(scannedLocs.size)],
      ['Register on the audit date', NUM(today.onAuditDate)],
      ['Marked unsold during the audit', NUM(today.unsold)]
    ])
    y = Math.max(y1, y2) + S[2]
    ink(C.subtle); doc.setFontSize(7.5); doc.setFontType('normal')
    doc.text(doc.splitTextToSize(
      'Period derived from AST scan times and PV capture dates on this voucher. Locations are as on the audit date, ' +
      'reconstructed from transfers made after it; a sale leaves no transfer, so the SOLD location is read as the register shows it.', CW),
    M, y + 8)
    y += S[6]
    const idxRows = sections.map((sec, i) => row([`A${i + 1}`, sec.title, NUM(sec.rows.length)]))
    idxRows.push(row(['', 'Total AST scans + PV items (physically audited)', NUM(scanned)], 'total'))
    autoTable(doc, {
      ...gridStyle,
      startY: y,
      head: [['', 'Section', 'Records']],
      body: body(idxRows),
      columnStyles: { 0: { cellWidth: 28 }, 2: { halign: 'right', cellWidth: 60 } },
      didParseCell: rowHook(idxRows),
      margin: { left: M, right: M }
    })
    y = doc.lastAutoTable.finalY + S[4]
    if (scanned !== audited) {
      warnBox('Annexure does not add up',
        `Its AST + PV records (${NUM(scanned)}) differ from the records audited (${NUM(audited)}).`)
    }

    sections.forEach((sec, i) => {
      if (y + 80 > BOTTOM) { doc.addPage(); y = S[8] }
      ink(C.strong); doc.setFontSize(10.5); doc.setFontType('bold')
      doc.text(pdf.clean(`A${i + 1}. ${sec.title} — ${NUM(sec.rows.length)}`), M, y + 10)
      y += 18
      autoTable(doc, {
        startY: y,
        theme: 'grid',
        styles: { fontSize: 6.6, cellPadding: 2.6, textColor: C.body, lineColor: C.border, lineWidth: 0.5, font: 'helvetica', overflow: 'linebreak' },
        headStyles: { fillColor: C.navy, textColor: C.ink0, fontStyle: 'bold', fontSize: 6.8 },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        head: [['#', 'Record', 'Code', 'Register asset', 'Equipment', 'Found at', 'Audit date', 'Reference']],
        body: sec.rows.map((r, n) => [
          String(n + 1), r.record, r.code, r.asset, r.model,
          r.sub ? `${r.loc} / ${r.sub}` : r.loc, r.time ? DATE(r.time) : '—', r.reference
        ]),
        columnStyles: {
          0: { cellWidth: 22, halign: 'right' }, 1: { cellWidth: 40 }, 2: { cellWidth: 50 }, 3: { cellWidth: 52 },
          6: { cellWidth: 44 }, 7: { cellWidth: 96 }
        },
        didParseCell: pdf.cellHook(),
        margin: { left: M, right: M, bottom: 50 }
      })
      annexRows += sec.rows.length
      y = doc.lastAutoTable.finalY + S[5]
    })
  }

  // Last, so it covers every [?] above.
  const unprintable = pdf.count()
  if (unprintable > 0) {
    y = doc.lastAutoTable.finalY + S[6]
    if (y + 40 > H - 50) { doc.addPage(); y = S[8] + S[6] }
    ink(C.warning); doc.setFontSize(10); doc.setFontType('bold')
    doc.text('Characters not printed', M, y)
    ink(C.body); doc.setFontSize(8); doc.setFontType('normal')
    doc.text(doc.splitTextToSize(unprintableNote(unprintable), CW), M, y + 14)
  }

  // ---------- footer on every page ----------
  const pages = doc.internal.getNumberOfPages()
  const footerVoucher = toPdfText(voucherLabel || '').text // already counted in the masthead
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i)
    draw(C.border); doc.setLineWidth(0.5)
    doc.line(M, H - 34, W - M, H - 34)
    ink(C.subtle); doc.setFontSize(7); doc.setFontType('normal')
    doc.text(`AssetPro · Reconciliation report · ${footerVoucher}`, M, H - 22)
    doc.text(`Page ${i} of ${pages}`, W - M, H - 22, { align: 'right' })
  }

  doc.save(`reconciliation-report-${moment().format('YYYYMMDD-HHmm')}.pdf`)
  return { missing: missing.length, listed: missing.length, annexed: annexRows, pages, unprintable }
}
